import { useMemo } from "react";
import { useChartDataSelector } from "../assetAllocation/editPortfolioGroup/useChartDataSelector";
import {
  AssetClass,
  AssetClassLoadType,
  useAssetClasses,
} from "@app/features/assets/useAssetClasses";
import {
  useCmaGenerateModelVersionListQuery,
  useCmaIndexComparisonRetrieveQuery,
} from "@app/api/carApi.generated";
import { formatISO, max } from "date-fns";
import { isDefined } from "@app/utils";
import { IndexCompareItem } from "@app/components/ChartIndexCompare";

export const useSimulationModelsIndexCompare = ({
  storageKey,
  data1Id,
  data2Id,
  data3Id,
}: {
  storageKey?: string;
  data1Id?: string;
  data2Id?: string;
  data3Id?: string;
}) => {
  const chartDataSelector = useChartDataSelector({
    data1DefaultId: data1Id,
    data2DefaultId: data2Id,
    data3DefaultId: data3Id,
    storageKey: storageKey ? `${storageKey}_chartDataSelector` : undefined,
  });

  const assetClasses = useAssetClasses({
    type: AssetClassLoadType.allFirm,
  });

  const modelVersions = useCmaGenerateModelVersionListQuery({
    id: "", // todo fix in swagger
  });

  const generateModelVersionId =
    modelVersions.data?.find((i) => i.is_default)?.id ?? "";

  const acItem1 = assetClasses.items.find(
    (ac) => ac.level4Id === chartDataSelector.data1Id,
  );
  const acItem2 = assetClasses.items.find(
    (ac) => ac.level4Id === chartDataSelector.data2Id,
  );
  const acItem3 = assetClasses.items.find(
    (ac) => ac.level4Id === chartDataSelector.data3Id,
  );

  const cmaDates = [
    acItem1?.cmaDataStartDate,
    acItem2?.cmaDataStartDate,
    acItem3?.cmaDataStartDate,
  ].filter(isDefined);

  const startDate =
    cmaDates.length > 0
      ? formatISO(max(cmaDates), { representation: "date" })
      : undefined;

  const endDate = formatISO(new Date(), { representation: "date" });

  const data = useCmaIndexComparisonRetrieveQuery(
    {
      assetClassIds: [
        chartDataSelector.data1Id,
        chartDataSelector.data2Id,
        chartDataSelector.data3Id,
      ]
        .filter(isDefined)
        .join(","),
      startDate,
    },
    { skip: !startDate },
  );

  return useMemo(() => {
    const getItem = (assetClass?: AssetClass): IndexCompareItem | undefined => {
      const chartData = data.data?.chart_data.find(
        (i) => i.asset_class_id === assetClass?.level4Id,
      );

      return chartData
        ? {
            name: assetClass?.name ?? "",
            regressionChartData: chartData.regression_chart ?? undefined,
            priceDrivenExpectedReturn: chartData.statistics?.mu,
            volatility: chartData.statistics?.sigma,
            // expectedTrendReturn: chartData.statistics?.
          }
        : undefined;
    };

    const clearSelectionItem: AssetClass = {
      level4Id: "",
      level1Id: "",
      firmAssetClassId: "",
      name: "Clear Selection",
      code: "",
      color: "",
      sort: -1,
      isSimplified: false,
      isRequired: false,
      isEquity: false,
      isRealEstate: false,
      isFixedIncome: false,
      isCommodity: false,
      isGold: false,
      isCash: false,
      isInflation: false,
      isDisabled: false,
    };

    return {
      isLoading: modelVersions.isLoading || data.isLoading,
      items: [
        clearSelectionItem,
        ...assetClasses.items.filter((i) => !i.isCash),
      ],
      chartDataSelector,
      startDate,
      item1: getItem(acItem1),
      item2: getItem(acItem2),
      item3: getItem(acItem3),
    };
  }, [
    modelVersions.isLoading,
    data.isLoading,
    data.data?.chart_data,
    assetClasses.items,
    chartDataSelector,
    startDate,
    acItem1,
    acItem2,
    acItem3,
  ]);
};
